import React, {useContext, useEffect, useRef, useState} from "react";
import {Form, Input, Tooltip} from "antd";

const EditableContext = React.createContext(null);

export const EditableRow = ({ index, ...props }) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    );
};

export const GenEditableCell = (props) => {
    const {title, editable, input, children, dataIndex, record, handleSave, ...restProps} = props;
    const [editing, setEditing] = useState(false);
    const form = useContext(EditableContext);
    const inputRef = useRef(null);

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editing]);

    const enableEdit = () => {
        setEditing(true);
        if (form) {
            form.setFieldsValue({
                [dataIndex]: record[dataIndex],
            });
        }
    };

    const save = async () => {
        try {
            if (form) {
                const values = await form.validateFields();
                const currentValue = form.getFieldValue(dataIndex);
                if (currentValue !== record[dataIndex]) {
                    handleSave({
                        ...record,
                        ...values,
                    });
                }
            }
        } catch (errInfo) {
            console.error("Save failed:", errInfo);
        } finally {
            setEditing(false);
        }
    };

    const focus = () => {
        document.addEventListener('mousedown', handleClickOutside);
    }

    const handleClickOutside = (clickEvent) => {
        const formItem = clickEvent.target.closest('.ant-form-item');
        if (formItem == null || !formItem.contains(clickEvent.target)) {
            document.removeEventListener('mousedown', handleClickOutside);
            setEditing(false);
        }
    };

    const blur = (blurEvent) => {
        const formItem = blurEvent.target.closest('.ant-form-item');
        const handleFocusIn = (focusEvent) => {
            document.removeEventListener('focusin', handleFocusIn);
            if (!formItem.contains(focusEvent.target)) {
                document.removeEventListener('mousedown', handleClickOutside);
                setEditing(false);
            }
        };
        document.addEventListener('focusin', handleFocusIn);
    }

    const getInputWithRef = () => {
        if (input) {
            return React.cloneElement(input, {
                ref: inputRef,
                onPressEnter: save,
            });
        }
        return <input/>;
    };

    let childNode = children;

    if (editable) {
        childNode = editing ? (
            <Form.Item
                name={dataIndex}
                rules={[]}
                onFocus={focus}
                onBlur={blur}
            >
                {getInputWithRef()}
            </Form.Item>
        ) : (
            <div
                className="editable-cell-value-wrap flexAlignCentre"
                onClick={enableEdit}
                onFocus={() => {
                    if (!editing) enableEdit();
                }}
                tabIndex={0}
                role="button"
                aria-label={`Edit ${title}`}
                style={{ cursor: "pointer" }}
            >
                {children}
            </div>
        );
    }

    return <td {...restProps}>{childNode}</td>;
};

export const EditableCell = ({
                          title,
                          editable,
                          children,
                          dataIndex,
                          record,
                          handleSave,
                          handleEditTest,
                          handleViewTest,
                          ...restProps
                      }) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef(null);
    const form = useContext(EditableContext);
    useEffect(() => {
        if (editing) {
            inputRef.current.focus();
        }
    }, [editing]);
    const toggleEdit = () => {
        setEditing(!editing);
        form.setFieldsValue({
            [dataIndex]: record[dataIndex],
        });
    };
    const save = async () => {
        try {
            const values = await form.validateFields();
            toggleEdit();
            handleSave({
                ...record,
                ...values,
            });
        } catch (errInfo) {
            console.error('Save failed:', errInfo);
        }
    };

    const editTest = ()=>{
        handleEditTest({...record})
    }

    const viewTest = ()=>{
        handleViewTest({...record});
    }

    let childNode = children;
    if (editable) {
        childNode = editing ? (
            <Form.Item
                style={{
                    margin: 0,
                }}
                name={dataIndex}
                rules={[
                    {
                        required: title !== 'Description',
                        message: `${title} is required.`,
                    },
                ]}
            >
                <Input ref={inputRef} onPressEnter={save} onBlur={save} />
            </Form.Item>
        ) : (

            childNode = title === 'Name' ? (( record.type === "Family" || record.type === 'Shared' ? (
                    // Name for test groups
                    <div className="editable-cell">
                        <div className="editable-cell-expand-button">
                            {children[0]}
                        </div>
                        {record.type === 'Shared' &&
                            // hide editable content for shared test family
                            <div
                                style={{
                                    marginLeft: 31
                                }}>
                                {children[1]}
                            </div>
                        }
                        {record.type === 'Family' && <div
                            className="editable-cell-value-wrap"
                            style={{
                                marginLeft: 20,
                                paddingRight: 24
                            }}
                            onClick={toggleEdit}
                        >
                            {children[1]}
                        </div>}
                    </div>
                ) : (
                    // Name for tests
                    <>
                        {record.editable
                            ?<Tooltip title={"Edit test"}><span className="ch-link" onClick={editTest}>{children}</span></Tooltip>
                            :<Tooltip title={"Preview test"}><span className="ch-link" onClick={viewTest}>{children}</span></Tooltip>
                        }
                    </>
                ) ))
                : (
                    //Description section
                <>
                    {record.type === 'Family' &&
                        <div
                            className="editable-cell-value-wrap"
                            style={{
                                paddingRight:24
                            }}
                            onClick={toggleEdit}
                        >
                        {children}
                        </div>
                    }
                    {record.type !== 'Family' &&
                        <div>
                            {children}
                        </div>
                    }
                </>


            )

        );
    }
    return <td {...restProps}>{childNode}</td>;
};
